import type { VehicleAvailability } from "@b2bportal/ground-api";
import { createSelector } from "@reduxjs/toolkit";
import type { PackageState } from "../../../../../../store/packages/slice";
import { getPackageCarPrice } from "../../utilities";
import { CarsAvailabilitySelectors } from "~core-cars/store";
import type { VehicleAvailabilityWithSupplier } from "~core-cars/store";
import type { PriceRange } from "~core-cars/types";
import {
  performBagsFilter,
  performCancellationPolicyFilter,
  performCarCompaniesFilter,
  performCarTypeFilter,
  performPassengersFilter,
  performSpecificationsFilter,
} from "~core-cars/utilities";

export const getPackageStateByVehicleId = (state: PackageState) => {
  return state.packages.cars.availability.data?.packageStateByVehicleId;
};

/**
 * Get price filter options based on incremental pricing for packages
 * This overrides the core selector to use incrementalChange instead of totalCharge
 */
export const getPackagePriceFilterOptions = createSelector(
  CarsAvailabilitySelectors.getCars,
  getPackageStateByVehicleId,
  (cars, packageStates) => {
    if (!packageStates) return null;

    // Use utility to get incremental prices, filter out nulls (not 0!)
    const carPrices = (cars.data ?? [])
      .map((car) => getPackageCarPrice(car, packageStates))
      .filter((price) => price !== null);

    if (carPrices.length === 0) return null;

    const initialPriceOption = carPrices[0];

    return carPrices.reduce(
      (acc, item) => ({
        min: item.value < acc.min.value ? item : acc.min,
        max: item.value > acc.max.value ? item : acc.max,
      }),
      { min: initialPriceOption, max: initialPriceOption }
    );
  }
);

/**
 * Filter function that uses incremental pricing for packages
 */
const performPackagePriceRangeFilter = (
  vehicle: VehicleAvailability,
  priceRange: PriceRange | null,
  packageStateByVehicleId?: {
    [vehicleId: string]: {
      price: { incrementalChange?: { fiat: { value: number } } };
    };
  }
): boolean => {
  if (priceRange == null) return true;

  // Use utility to get incremental price
  const price = getPackageCarPrice(vehicle, packageStateByVehicleId);
  if (price === null) return false;

  if (price.value < priceRange.min || price.value > priceRange.max) {
    return false;
  }

  return true;
};

/**
 * Get filtered cars based on package incremental pricing
 * This overrides the core selector to use incrementalChange for price filtering
 */
export const getPackageFilteredCars = createSelector(
  CarsAvailabilitySelectors.getCars,
  CarsAvailabilitySelectors.getFilters,
  CarsAvailabilitySelectors.getSuppliers,
  getPackageStateByVehicleId,
  (cars, filters, suppliers, packageStates) => {
    if (cars.data == null || suppliers == null) return [];

    const filteredCars: VehicleAvailabilityWithSupplier[] = (cars.data ?? [])
      .filter((car) => {
        const filtersArray = [
          performCarTypeFilter(car, filters.carType),
          performPackagePriceRangeFilter(
            car,
            filters.priceRange,
            packageStates
          ),
          performCarCompaniesFilter(car, filters.supplier),
          performSpecificationsFilter(car, filters.specification),
          performBagsFilter(car, filters.minimumBags),
          performPassengersFilter(car, filters.passenger),
          performCancellationPolicyFilter(car, filters.freeCancellation),
        ];

        return filtersArray.every((filterCondition) => filterCondition);
      })
      .map((car) => {
        return {
          ...car,
          supplier: suppliers[car.supplierRef],
        };
      });

    return filteredCars;
  }
);

/**
 * Check if price filter has been set for packages
 * This overrides the core selector to properly detect active filters with incremental pricing
 */
export const getPackageHasSetPriceFilter = createSelector(
  CarsAvailabilitySelectors.getPriceFilter,
  getPackagePriceFilterOptions,
  (priceFilter, packagePriceOptions) => {
    if (!packagePriceOptions) return false;

    return (
      priceFilter?.max !== packagePriceOptions.max.value ||
      priceFilter?.min !== packagePriceOptions.min.value
    );
  }
);

/**
 * Check if any filters are active for packages
 * Uses package-specific price filter detection
 */
export const getPackageHasFilters = createSelector(
  getPackageHasSetPriceFilter,
  CarsAvailabilitySelectors.hasSpecificationFilter,
  CarsAvailabilitySelectors.hasMinimumBagsFilter,
  CarsAvailabilitySelectors.getFilters,
  (hasPriceFilter, hasSpecificationFilter, hasMinimumBagsFilter, filters) => {
    return (
      hasPriceFilter ||
      hasMinimumBagsFilter ||
      hasSpecificationFilter ||
      filters.freeCancellation ||
      filters.carType.length > 0 ||
      filters.passenger.length > 0 ||
      filters.supplier.length > 0
    );
  }
);

/**
 * Get total count of active filters for packages
 * Uses package-specific price filter detection
 */
export const getPackageTotalFilterCount = createSelector(
  getPackageHasSetPriceFilter,
  CarsAvailabilitySelectors.hasSpecificationFilter,
  CarsAvailabilitySelectors.hasMinimumBagsFilter,
  CarsAvailabilitySelectors.getFilters,
  (hasPriceFilter, hasSpecificationFilter, hasMinimumBagsFilter, filters) => {
    const activeFilters = [
      hasPriceFilter,
      hasMinimumBagsFilter,
      hasSpecificationFilter,
      filters.freeCancellation,
      filters.carType.length > 0,
      filters.passenger.length > 0,
      filters.supplier.length > 0,
    ];

    return activeFilters.filter(Boolean).length;
  }
);
